/**
 * F 324 — THE CATALOGUE: what THIS app's settings are.
 *
 * The catalogue is the app-specific half of the pattern. The renderer is
 * generic; the catalogue says what exists. A consumer copies the renderer
 * and writes its own catalogue.
 *
 * EVERY SETTINGS.TSX PREFERENCE, AND WHAT BECAME OF IT
 *
 * The old page held a `UserPreferences` interface with ten fields. Nine were
 * rendered; one pair were not. F 324 enumerated all ten before touching any
 * of them:
 *
 *   field            type                  default      rendered?  decision
 *   ───────────────  ────────────────────  ───────────  ─────────  ──────────────────
 *   theme            light|dark|system     'system'     yes        NODE
 *   fontSize         number                16           yes        NODE
 *   compactMode      boolean               false        yes        NODE
 *   defaultView      string                'Page View'  yes        NODE
 *   autoSave         boolean               true         yes        NODE
 *   showSidebar      boolean               true         yes        NODE
 *   sidebarPosition  left|right            'left'       yes        NODE
 *   aiAssistant      boolean               false        yes        NODE
 *   primaryColor     string                '#007bff'    NO         LEFT — dead field
 *   accentColor      string                '#00d4ff'    NO         LEFT — dead field
 *
 * `primaryColor` and `accentColor` were declared on the interface and given
 * defaults, and appeared in NO control and in no read anywhere in the repo.
 * They were carried in the localStorage blob and nothing ever looked at them.
 * They get no node: a node is a permanent home in the graph, and a value
 * nothing reads does not deserve one. They are not deleted from the seed
 * path either — the seed simply never writes them.
 *
 * `theme` and `sidebarPosition` and `defaultView` are ENUMS, and a generic
 * renderer cannot know an enum's options. The convention that solves it:
 * an enum node's value is `{ value, options }` and its `valueType` is 'json'.
 * The renderer sees `options` and draws a Select. A consumer with no enums
 * never sees this shape at all.
 */

import type { Setting } from '../../types/models/Setting';
import type { SettingStore } from './SettingStore';
import type { LegacyPreferences } from './settingHost';
import { makeTelemetrySetting } from '../telemetry/telemetrySetting';

/** A catalogue entry: a setting, plus how to present it. */
export interface SettingCatalogueEntry {
  key: string;
  label: string;
  valueType: Setting['valueType'];
  default: unknown;
  /** The options, for an enum. Present -> the renderer draws a Select. */
  options?: string[];
  description?: string;
  /** Whether the page offers an editor for it. */
  editable?: boolean;
  /** Which field of the legacy localStorage blob this setting came from. */
  legacyField?: string;
}

export const APP_PREFERENCE_GROUP = 'app';

/** The namespace every app-owned preference lives under. */
export const APP_PREFERENCE_PREFIX = 'app.';

/**
 * THIS APP'S CATALOGUE.
 *
 * The values here are the ones the old hardcoded page used, unchanged. The
 * old page's `defaultPreferences` said `fontSize: 16`; this says 16. The
 * separate `interaction-modules.ts` settings said 14 — that mechanism is
 * unwired, and this catalogue does not adopt its number.
 */
export const APP_SETTINGS_CATALOGUE: readonly SettingCatalogueEntry[] = [
  {
    key: 'app.appearance.theme',
    legacyField: 'theme',
    label: 'Theme',
    valueType: 'string',
    default: 'system',
    options: ['light', 'dark', 'system'],
    description: 'The colour scheme FreshCards opens in.',
  },
  {
    key: 'app.appearance.fontSize',
    legacyField: 'fontSize',
    label: 'Font size',
    valueType: 'number',
    default: 16,
    description: 'Base font size in pixels. Applied to the document root.',
  },
  {
    key: 'app.appearance.compactMode',
    legacyField: 'compactMode',
    label: 'Compact mode',
    valueType: 'boolean',
    default: false,
    description: 'Tighter spacing throughout the app.',
  },
  {
    key: 'app.workspace.defaultView',
    legacyField: 'defaultView',
    label: 'Default view',
    valueType: 'string',
    default: 'Page View',
    options: ['Page View', 'Board View', 'Calendar View', 'Timeline View'],
    description: 'Which view a database opens in.',
  },
  {
    key: 'app.workspace.autoSave',
    legacyField: 'autoSave',
    label: 'Auto-save',
    valueType: 'boolean',
    default: true,
    description: 'Save changes without being asked.',
  },
  {
    key: 'app.workspace.showSidebar',
    legacyField: 'showSidebar',
    label: 'Show sidebar',
    valueType: 'boolean',
    default: true,
    description: 'Show the sidebar.',
  },
  {
    key: 'app.workspace.sidebarPosition',
    legacyField: 'sidebarPosition',
    label: 'Sidebar position',
    valueType: 'string',
    default: 'left',
    options: ['left', 'right'],
    description: 'Which side the sidebar sits on.',
  },
  {
    key: 'app.integrations.aiAssistant',
    legacyField: 'aiAssistant',
    label: 'AI assistant',
    valueType: 'boolean',
    default: false,
    description: 'Show the AI assistant.',
  },
];

/** The telemetry signal F 322 built. It is a node like any other. */
export const TELEMETRY_SIGNAL = 'cpu';

/**
 * Every node this app ships, as `Setting` entities.
 *
 * A catalogue entry becomes a node with the value shape the renderer
 * expects: a plain value for scalars, `{ value, options }` for an enum.
 */
export function appSettingNodes(): Setting[] {
  const nodes: Setting[] = [];

  for (const entry of APP_SETTINGS_CATALOGUE) {
    nodes.push({
      id: `global::${entry.key}`,
      fcid: `fcid://setting/global::${entry.key}`,
      key: entry.key,
      label: entry.label,
      value: enumValue(entry),
      valueType: entry.options ? 'json' : entry.valueType,
      scope: 'global',
      scopeId: null,
      default: enumValue(entry),
      description: entry.description,
      editable: entry.editable ?? true,
    });
  }

  // The telemetry retention setting, built by F 322, is a node like any other.
  // It is listed here so the page shows it; the mechanism lives in
  // `src/core/telemetry/telemetrySetting.ts` and is NOT duplicated.
  nodes.push(makeTelemetrySetting({ signal: TELEMETRY_SIGNAL }));

  return nodes;
}

/** An enum's value carries its options, so a generic renderer can offer them. */
function enumValue(entry: SettingCatalogueEntry): unknown {
  return entry.options ? { value: entry.default, options: entry.options } : entry.default;
}

/**
 * F 324 — THE WRITE PATH.
 *
 * The page's write is a named unit rather than an inline call, because two
 * rules live in it and both are invisible when it is inline:
 *
 *   1. the graph decides the TYPE (the node's declared type wins);
 *   2. the graph decides the SHAPE (an enum stays an enum);
 *   3. a `global` write is an operator act, so it must be approved.
 *
 * A consumer copying the pattern gets all three by calling this, instead of
 * rediscovering each one as a silent no-op.
 */
export function writeAppSetting(
  store: SettingStore,
  key: string,
  value: unknown,
): { ok: boolean; code?: string } {
  const declared = store.getSettingEntity(key, 'global', null);
  const valueType = declared ? declared.valueType : 'json';
  const shaped = declared ? coerceSettingValue(declared.value, value) : value;
  const result = store.setSetting({
    key,
    value: shaped,
    valueType,
    scope: 'global',
    operatorApproved: true,
  });
  // This repo's tsconfig leaves `strictNullChecks` off, which disables
  // discriminated-union narrowing — `result.code` is not reachable through
  // `result.ok` here, so the error branch is reached by cast.
  if (result.ok) return { ok: true };
  return { ok: false, code: (result as Extract<typeof result, { ok: false }>).code };
}

/**
 * F 324 — THE DEFAULT SEED.
 *
 * A FRESH profile has no graph nodes and no legacy blob. If the store is
 * left empty, the page has nothing to render: the telemetry setting is
 * invisible, and the app's own preferences are missing rather than default.
 *
 * So a fresh store is seeded from the CATALOGUE — the app's own statement of
 * what settings exist and what they default to. This is the only place the
 * catalogue's defaults become real values.
 */
export function seedCatalogueDefaults(store: SettingStore): number {
  let written = 0;
  for (const node of appSettingNodes()) {
    const result = store.setSetting({
      key: node.key,
      value: node.value,
      valueType: node.valueType,
      scope: node.scope,
      scopeId: node.scopeId,
      operatorApproved: true,
    });
    if (result.ok) written += 1;
  }
  return written;
}

/**
 * F 324 — THE SEED.
 *
 * The seed lives in the APP layer, not the host, because only the app knows
 * the shape of its own settings.
 *
 * It seeds from the CATALOGUE'S OWN NODE, not from an ad-hoc node. That is
 * the fix for a bug this dispatch found in its own first draft: a seed that
 * wrote a bare string for an enum key produced a DIFFERENT value shape than
 * the catalogue does, so the same setting rendered as a Select on a fresh
 * install and as a text box on a migrated one. Seeding the catalogue's node
 * makes the two paths identical by construction.
 */
export function seedAppSettings(store: SettingStore, legacy: LegacyPreferences): number {
  let written = 0;
  for (const node of appSettingNodes()) {
    const entry = APP_SETTINGS_CATALOGUE.find((c) => c.key === node.key);
    if (!entry) continue;              // the telemetry node is not a legacy field
    const legacyValue = legacy[entry.legacyField as keyof LegacyPreferences];
    if (legacyValue === undefined || legacyValue === null) continue;
    const value = entry.options
      ? { value: String(legacyValue), options: entry.options }
      : legacyValue;
    const result = store.setSetting({
      key: node.key,
      value,
      valueType: node.valueType,
      scope: node.scope,
      scopeId: node.scopeId,
      // The app's own settings page is the operator surface for these keys.
      operatorApproved: true,
    });
    if (result.ok) written += 1;
  }
  return written;
}

/**
 * F 324 — THE SHAPE COERCION.
 *
 * `SettingStore` accepts ANY value into a node declared `json`. It will not
 * stop a caller writing the bare string `'dark'` into an enum node. That is
 * not a bug in the store — a json node legitimately holds a bare string —
 * but it is a trap for the page: the renderer decides "Select or text box"
 * by asking whether the STORED VALUE carries its options. A bare string does
 * not, so a theme changed through a bespoke control would silently turn the
 * field into a text area on the next render.
 *
 * So the writer coerces to the shape the node already holds. The options
 * travel WITH THE VALUE, which is why the value carries them.
 */
export function coerceSettingValue(existing: unknown, next: unknown): unknown {
  if (isEnumValue(existing)) {
    const opts = (existing as { options: string[] }).options;
    // An enum value may arrive as the bare scalar (a bespoke control) or as
    // the full shape (the generic panel). Both are accepted; both are written
    // back in the full shape.
    const scalar = isEnumValue(next) ? (next as { value: unknown }).value : next;
    return { value: scalar, options: opts };
  }
  return next;
}

/** True when a stored value is the enum shape the renderer understands. */
export function isEnumValue(v: unknown): v is { value: string; options: string[] } {
  if (!v || typeof v !== 'object') return false;
  const e = v as { value?: unknown; options?: unknown };
  return typeof e.value === 'string' && Array.isArray(e.options) && e.options.every((o) => typeof o === 'string');
}

/** The scalar value of a node, unwrapping the enum shape. */
export function scalarValue(v: unknown): unknown {
  return isEnumValue(v) ? v.value : v;
}
